import { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { SiX, SiFacebook, SiLinkedin, SiInstagram } from 'react-icons/si';
import { Button } from '@/components/ui/button';
import SammoLogo from '@/components/SammoLogo';
import { ROUTE_PATHS, NAV_ITEMS } from '@/lib/index';
import { springPresets } from '@/lib/motion';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col">
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 h-20 ${isScrolled ? 'bg-background/95 backdrop-blur-md shadow-lg border-b border-border' : 'bg-transparent'}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={springPresets.gentle}
      >
        <div className="container mx-auto px-4 h-full">
          <div className="flex items-center justify-between h-full">
            <Link to={ROUTE_PATHS.HOME} className="group">
              <SammoLogo variant="default" size="md" className="transition-transform duration-300 group-hover:scale-105" />
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'text-accent bg-accent/10'
                        : 'text-foreground hover:text-accent hover:bg-accent/5'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Button
                asChild
                variant="outline"
                className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
              >
                <Link to={ROUTE_PATHS.FOR_BUSINESS}>For Business</Link>
              </Button>
              <Button
                asChild
                className="bg-accent hover:bg-accent/90 text-accent-foreground"
              >
                <Link to={ROUTE_PATHS.FAMILY_ACCESS}>Start Free Trial</Link>
              </Button>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              className="fixed top-20 left-0 right-0 bottom-0 bg-background z-40 lg:hidden overflow-y-auto"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={springPresets.gentle}
            >
              <nav className="container mx-auto px-4 py-8 flex flex-col gap-2">
                {NAV_ITEMS.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                        isActive
                          ? 'text-accent bg-accent/10'
                          : 'text-foreground hover:text-accent hover:bg-accent/5'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <div className="flex flex-col gap-3 mt-6">
                  <Button
                    asChild
                    variant="outline"
                    className="w-full border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Link to={ROUTE_PATHS.FOR_BUSINESS}>For Business</Link>
                  </Button>
                  <Button
                    asChild
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Link to={ROUTE_PATHS.FAMILY_ACCESS}>Start Free Trial</Link>
                  </Button>
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <main className="flex-1 pt-20">{children}</main>

      <footer className="bg-primary text-primary-foreground mt-24">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
            <div className="lg:col-span-2">
              <Link to={ROUTE_PATHS.HOME} className="inline-block mb-4">
                <SammoLogo variant="white" size="md" />
              </Link>
              <p className="text-primary-foreground/80 text-sm mb-6">
                Practical support for everyday African life. Helping families across money, education, health, and daily living.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://x.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/60 hover:text-accent transition-colors"
                  aria-label="X (Twitter)"
                >
                  <SiX size={20} />
                </a>
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/60 hover:text-accent transition-colors"
                  aria-label="Facebook"
                >
                  <SiFacebook size={20} />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/60 hover:text-accent transition-colors"
                  aria-label="LinkedIn"
                >
                  <SiLinkedin size={20} />
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/60 hover:text-accent transition-colors"
                  aria-label="Instagram"
                >
                  <SiInstagram size={20} />
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-sm uppercase tracking-wider mb-4">Product</h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to={ROUTE_PATHS.PLATFORM}
                    className="text-primary-foreground/80 hover:text-accent text-sm transition-colors"
                  >
                    Platform
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.ASSISTANTS}
                    className="text-primary-foreground/80 hover:text-accent text-sm transition-colors"
                  >
                    Assistants
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.FAMILY_ACCESS}
                    className="text-primary-foreground/80 hover:text-accent text-sm transition-colors"
                  >
                    Family Access
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm uppercase tracking-wider mb-4">Solutions</h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to={ROUTE_PATHS.FOR_BUSINESS}
                    className="text-primary-foreground/80 hover:text-accent text-sm transition-colors"
                  >
                    For Business
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.SCHOOLS}
                    className="text-primary-foreground/80 hover:text-accent text-sm transition-colors"
                  >
                    Schools
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.PARTNERS}
                    className="text-primary-foreground/80 hover:text-accent text-sm transition-colors"
                  >
                    Partners
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm uppercase tracking-wider mb-4">Company</h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to={ROUTE_PATHS.ABOUT}
                    className="text-primary-foreground/80 hover:text-accent text-sm transition-colors"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.CONTACT}
                    className="text-primary-foreground/80 hover:text-accent text-sm transition-colors"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-sm uppercase tracking-wider mb-4">Legal</h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href="#"
                    className="text-primary-foreground/80 hover:text-accent text-sm transition-colors"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-primary-foreground/80 hover:text-accent text-sm transition-colors"
                  >
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-primary-foreground/20 mt-12 pt-8 text-center">
            <p className="text-primary-foreground/60 text-sm">
              © 2026 Sammo. All rights reserved. Built for Africa.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}